import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TranslatableLabel } from '#src/app/shared/hooks/i18n';

const RESET_LABEL_TIMER = 3000;

type ShareData = {
	buttonLabel: TranslatableLabel;
	clickedState: boolean;
};
type ShareActions = { afterShare: (success: boolean) => void };
type UseDefaultShareButtonResult = ModelHookResult<ShareData, ShareActions>;
type UseShareButtonArgs = {
	label: TranslatableLabel;
	successLabel?: TranslatableLabel;
};

/**
 * Drives the button's transient "shared" label. Mirrors `useSocialIconWithSuccessState`: the state is
 * tri-state so a failed share is distinguishable from one that never happened, and only a success
 * swaps the label — otherwise a denied clipboard write still reads "Copied!".
 */
export function useShareChannelButton({ label, successLabel }: UseShareButtonArgs): UseDefaultShareButtonResult {
	const [successState, setSuccessState] = useState<boolean | undefined>(undefined);

	const afterShare = useCallback((success: boolean) => {
		setSuccessState(success);
	}, []);

	useEffect(() => {
		if (typeof successState === 'undefined') return undefined;

		const timeout = window.setTimeout(() => setSuccessState(undefined), RESET_LABEL_TIMER);

		return () => window.clearTimeout(timeout);
	}, [successState]);

	const clickedState = successState === true && !!successLabel;

	const buttonLabel = useMemo(
		() => (successState === true && successLabel ? successLabel : label),
		[successState, successLabel, label],
	);

	return {
		data: { buttonLabel, clickedState },
		actions: { afterShare },
	};
}
